import React, { useState } from "react";
import { rng, stringUtil, SurveyReport } from '../../../amorg-common';
import { AggregatedSurveyReport } from '../../../amorg-common';
import { QuestionStats } from '../../../amorg-common';
import { LoadingPageOverlay } from "../../../designSystem/LoadingPageOverlay";
import { Button } from "../../../designSystem/button/Button";
import { Card } from '../../../designSystem/Card';
import { H2 } from "../../../designSystem/typography/Heading";
import { ReportSummary } from "../../surveys/report/ReportSummary";
import { createSurvey} from "../../../api/surveyClientApi";
import { ActivityDetailsPanel } from "../common/ActivityDetailsPanel";
import { WorkplaceAssessmentComponentProps } from './WorkplaceAssessmentComponentProps';
import { timeToClose } from "../../../utils/date";
import { Survey, surveyHelper, getSurveyStatus } from '../../../amorg-common';
import { getActionPlansByActivityId } from "../../../api/actionPlanClientApi";
import { useHistory } from "react-router-dom";
import { ActionPlanListView } from "../../../amorg-common/DTOs/ActionPlanListView";
import { Flex } from "../../../designSystem/compositeComponents/FlexHelpers";
import { AmorgMenu, AmorgMenuContent } from "../../../designSystem/amorgMenu";
import { ActivityHeader } from "../common/ActivityHeader";
import { CollapsiblePanel } from "../../../designSystem/collapsible/CollapsiblePanel";
import { TitleContainer } from "../common/AgendaMeetingActivityView";
import { updateActivity } from "../../../api/activityClientApi";
import { MessageCard } from "../../../designSystem/Message";



type State = {
    questionReports: QuestionStats[],
    actionPlans: ActionPlanListView[]
}
export function OverviewPanel(props:WorkplaceAssessmentComponentProps): JSX.Element {
    const [state, setState] = React.useState<State>(
        {
            questionReports: [],
            actionPlans: []
        });

    React.useEffect(() => {
        const getData = async () => {
            if(props.surveyLoading){
                setState({questionReports:[], actionPlans:[]})
            }
            else if(props.survey){
                setState({
                    questionReports:buildReports(props.survey, props.surveyReport),
                    actionPlans:await getActionPlansByActivityId(props.survey.activityId)
                })
            }
        };
        getData();
        
    }, [props.survey, props.surveyReport]);

    function buildReports(survey:Survey, surveyReport:SurveyReport) {
        const data = new AggregatedSurveyReport(
            survey,
            surveyReport,
            survey.segmentations.length === 0
                ? []
                : surveyReport.selectionReports.map(sr => sr.segments)
        ).data;
        return data;
    }

    async function handleClickCreateSurvey() {
        const id = rng.getRandomId();
        const result = await createSurvey(id, props.activity.id);
        if (result.isSuccess) {
            props.onSurveyUpdated();
        }
    }

    async function updateTitle(value: string) {
        await updateActivity(props.activity.id, value, props.activity.description, props.activity.start, props.activity.end, props.activity.location);
        props.onActivityUpdated();
    }

    return (
        <>
            <LoadingPageOverlay loading={!props.activity || props.surveyLoading} />

            <ActivityHeader
                activity={props.activity}
                deleteConfirmationTextInModal="Tilhørende handleplaner, spørgeskemaer samt besvarelser bliver også slettet." />

            <Flex flexDirection='column'>
                    
                <CollapsiblePanel 
                    defaultOpen={true}
                    openHeader={
                        <TitleContainer title={props.activity.title} updateTitle={updateTitle} /> 
                    }
                    closedOrDefaultHeader={
                        <TitleContainer title={props.activity.title} updateTitle={updateTitle} /> 
                }>
                        
                    <ActivityDetailsPanel
                        componentTitle="APV Info"
                        periodSelection="date"
                        activity={props.activity}
                        descriptionText="APV beskrivelse"
                        onActivityUpdated={props.onActivityUpdated} 
                        users={props.users}
                        />    
                    
                </CollapsiblePanel>
                        
            </Flex>

            {props.activity &&
                <>
                    
                    <Flex flexDirection="column" gap={16}>
                        {props.survey &&
                            <Card>
                                <SurveyMenu survey={props.survey} 
                                surveyUrl={
                                    `${window.location.protocol}//${window.location.host}/surveys/${props.survey?.id || ''}@${props.activeUser.siteId}`
                                } />
                            </Card>
                        }
                        {!props.survey &&
                            <>
                            <MessageCard variant="info">
                                Der er ikke oprettet et spørgeskema for APVen.
                            </MessageCard>
                            
                            <Button
                                variant='light'
                                size='magnum'
                                onClick={handleClickCreateSurvey}
                                text={"Opret spørgeskema"} 
                                icon="actions_plus"
                                />
                            </>
                        }
                    </Flex>

                    {props.survey &&
                        <>
                            <Card marginTop={20}>
                                <H2>Besvarelser</H2>
                                <Card variant="outlined" marginTop={20}>
                                    <ReportSummary survey={props.survey} questionReports={state.questionReports} excludeQuestionSummary />
                                </Card>
                            </Card>

                            <Card marginTop={20}>
                                <H2>Handleplaner</H2>
                                <ActionPlanListing actionPlans={state.actionPlans} />
                            </Card>
                        </>
                    }
                </>
            }
        </>
    );
}

type SurveyMenuProps = {
    survey:Survey,
    surveyUrl:string
}
function SurveyMenu(props:SurveyMenuProps):JSX.Element
{
    const surveyMenuContent:AmorgMenuContent[] = [];
    const [showToolTip, setShowToolTip] = useState(false)


    function ToolTip(){
        navigator.clipboard.writeText(props.surveyUrl)
        setShowToolTip(true)
        setTimeout(() => {
            setShowToolTip(false)
        }, 2000);
        
    }

    if(!props.survey){
        surveyMenuContent.push(
            {
                type:'leadingContent',
                styleVariant:'destructive',
                leadingContent:{ type:'icon', iconName:'actions_close'},
                primaryContent:{ type:'text', text:'Spørgeskema ikke oprettet'},
            }
        );
    }
    else {
        surveyMenuContent.push({
            type:'leadingTrailingContent',
            styleVariant:'default',
            leadingContent:{ type:'icon', iconName:'actions_done'},
            primaryContent:{ type:'text', text:'Spørgeskema oprettet'},
            trailingContent:{type:'text', text:`${surveyHelper.getAllSurveyQuestions(props.survey).length} spørgsmål`}
        });
        surveyMenuContent.push(
            getSurveyStatus(props.survey).canSubmitAnswer
                ? {
                    type:'leadingTrailingContent',
                    styleVariant:'default',
                    leadingContent:{ type:'icon', iconName:'interface_glyph_hourglass'},
                    primaryContent:{ type:'text', text:'Spørgeskemaet er åbent for besvarelser'},
                    trailingContent:{type:'text', text:timeToClose(props.survey.end, new Date())}
                }
                : {
                    type:'leadingContent',
                    styleVariant:'default',
                    leadingContent:{ type:'icon', iconName:'interface_glyph_hourglass'},
                    primaryContent:{ type:'text', text:'Spørgeskemaet er lukket for besvarelser'},
                }
        );
        surveyMenuContent.push({
            type:'separator'
        });
        surveyMenuContent.push({
            type:'leadingContent',
            styleVariant:'emphasized',
            leadingContent:{type:'icon', iconName:'interface_actions_copy'},
            primaryContent:{ type:'text',text:'Kopier link til spørgeskema'},
            toolTip: showToolTip ? "Linket er nu kopieret" : "" ,
            onClick: () => ToolTip()
        });
    }

    return (
        <AmorgMenu content={surveyMenuContent} />
    );
}

type ActionListingMenuProps = {
    actionPlans:ActionPlanListView[]
}
function ActionPlanListing(props:ActionListingMenuProps):JSX.Element
{
    const history = useHistory();

    if(props.actionPlans.length === 0){
        return <i>Der er ikke oprettet handleplaner for denne APV</i>;
    }
    else {
        const surveyMenuContent:AmorgMenuContent[] = props.actionPlans.map(ap => 
            ({
                type:'leadingTrailingContent',
                styleVariant:'default',
                leadingContent:{ type:'icon', iconName:'fact_check' },
                primaryContent:{ type:'text', text:stringUtil.capTextLength(ap.title, 25)},
                trailingContent:{ type:'text',  text:ap.owner ? stringUtil.capTextLength(ap.owner.fullName, 25) : '-'},
                onClick:() => history.push(`/workplaceassessments/${ap.activityId}?tab=actionplans&actionPlanId=${ap.id}`)
            } as AmorgMenuContent)
        );

        return (
            <AmorgMenu content={surveyMenuContent} />
        );
    }
}